import userApi from '@api/userApi';
import ItemEditor from '@components/itemEditor';
import OrgRolesSelect from '@components/orgRolesSelect';
import useAuth from '@hooks/useAuth';
import { t } from '@i18n';
import { Alert, TextInput } from '@mantine/core';
import type { PublicUser } from '@models/user';
import { IconInfoCircle } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { createForm } from 'cross-state/react';
import { pick } from 'radashi';

export const Route = createFileRoute('/profile/users/$id')({
  component: UserEditor,
});

const userForm = createForm<PublicUser>({
  defaultValue: {
    id: '',
    userName: '',
    firstName: '',
    lastName: '',
    orgRoles: [],
  },
  validations: {
    userName: {
      required: (x, { original }) => (original ? true : !!x),
      email: (x) => !!x?.includes('@'),
    },
    firstName: { required: (x) => !!x },
    lastName: { required: (x) => !!x },
    orgRoles: { atLeastOneOrg: (x) => !!x?.length },
  },
});

function UserEditor() {
  const { hasClaim } = useAuth();

  return (
    <ItemEditor
      domain="users"
      api={userApi}
      readonly={(user) =>
        !hasClaim('users/write', !user ? 'any' : user.orgRoles.length === 0 ? 'any' : user.orgRoles.map((x) => x.orgId))
      }
      form={userForm}
      name={(user) => user.userName ?? ''}
      prepareDraft={(user) => {
        return pick(
          user,
          user.id
            ? ['id', 'firstName', 'lastName', 'orgRoles']
            : ['id', 'userName', 'firstName', 'lastName', 'orgRoles'],
        );
      }}
    >
      {({ isNew }) => (
        <>
          <userForm.Field
            name="userName"
            render={(props) => (
              <TextInput
                {...props}
                formNoValidate
                label={t('users.properties.email.label')}
                description={t('users.properties.email.description')}
                withAsterisk
                autoComplete="email"
                spellCheck={false}
                disabled={!isNew}
              />
            )}
          />

          <userForm.Field
            name="firstName"
            render={(props) => (
              <TextInput
                {...props}
                value={props.value ?? ''}
                label={t('users.properties.firstName.label')}
                withAsterisk
                autoComplete="off"
                spellCheck={false}
              />
            )}
          />

          <userForm.Field
            name="lastName"
            render={(props) => (
              <TextInput
                {...props}
                value={props.value ?? ''}
                label={t('users.properties.lastName.label')}
                withAsterisk
                autoComplete="off"
                spellCheck={false}
              />
            )}
          />

          <userForm.Field
            name="orgRoles"
            render={(props) => <OrgRolesSelect {...props} value={props.value ?? []} required />}
          />

          <div css={{ flex: 1000 }} />

          {isNew && <Alert icon={<IconInfoCircle />}>{t('users.newUserHint')}</Alert>}
        </>
      )}
    </ItemEditor>
  );
}
